import React, { useState, useMemo, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  updateCreationQualificationSteps,
  removeCreationQualificationStep,
} from "../../../store/slices/qualificationStepSlice";
import { Plus, X, Pencil, Trash2 } from "lucide-react";
import { renderGrid } from "../../common/FormRenderer";
import { validateProjectQualStepEditForm } from "../../../config/projectQualStepEdit.validation";

const QualificationSteps = () => {
  const dispatch = useDispatch();
  const qualificationSteps = useSelector(
    (state) => state.qualificationStep.creation.qualificationSteps,
  );
  const objectives = useSelector(
    (state) => state.projectObjective.creation.objectives,
  );
  const metadata = useSelector((state) => state.project.creation.metadata);
  const isCreating = useSelector((state) => state.navigation.isCreating);
  const [showModal, setShowModal] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [form, setForm] = useState({
    name: "",
    project: metadata?.name || "",
    objective: "",
    passingScore: "",
    status: "Draft",
    qualificationStep: "",
    funnel: "A",
    stepNumber: "",
    numberOfAttempts: "1",
    estimatedTime: "",
    createdBy: "",
    lastModifiedBy: "",
  });

  const objectiveOptions = useMemo(() => {
    const opts = [];
    if (objectives?.objectiveName) {
      opts.push({
        label: objectives.objectiveName,
        value: objectives.objectiveId,
      });
    }
    if (Array.isArray(objectives)) {
      objectives.forEach((obj) => {
        if (obj.objectiveName)
          opts.push({
            label: obj.objectiveName,
            value: obj.objectiveId,
          });
      });
    }
    return opts;
  }, [objectives]);

  // Keep Project display and Id in sync with Step 1 metadata
  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      project: metadata?.name || prev.project || "",
      projectId: metadata?.id || prev.projectId || "",
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metadata?.name, metadata?.id]);

  // Also sync any already-added steps' project display name during wizard create
  useEffect(() => {
    if (!isCreating) return;
    const display = metadata?.name || "";
    if (!display) return;
    if (
      Array.isArray(qualificationSteps) &&
      qualificationSteps.some((s) => (s?.project || "") !== display)
    ) {
      const updated = qualificationSteps.map((s) => ({
        ...s,
        project: display,
      }));
      dispatch(updateCreationQualificationSteps(updated));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metadata?.name, isCreating, qualificationSteps?.length]);
  const fields = [
    {
      label: "Project",
      path: "project",
      type: "text",
      readOnly: true,
      required: true,
      info: "Auto-filled from Step 1. Edit Project Metadata to change.",
    },
    {
      cols: 2,
      fields: [
        {
          label: "Project Objective",
          path: "objective",
          type: "select",
          options: [...objectiveOptions],
          info: "Select project objective",
        },
      ],
    },
    {
      cols: 2,
      fields: [
        {
          label: "Status",
          path: "status",
          type: "select",
          required: true,
          options: ["Draft", "Active", "Inactive", "Archived"],
        },
        {
          label: "Funnel",
          path: "funnel",
          type: "select",
          required: true,
          options: ["A", "B", "C", "D", "E"],
        },
      ],
    },
    {
      cols: 2,
      fields: [
        {
          label: "Step Number",
          path: "stepNumber",
          type: "number",
          required: true,
          info: "Order of this qualification step",
        },
        {
          label: "Number of Attempts",
          path: "numberOfAttempts",
          type: "number",
          required: true,
          info: "Max attempts allowed",
        },
      ],
    },
    {
      label: "Qualification Step",
      path: "qualificationStep",
      type: "searchable",
      searchType: "qualificationStep",
      required: true,
      info: "Search and select a qualification step",
    },
    {
      cols: 2,
      fields: [
        {
          label: "Passing Score",
          path: "passingScore",
          type: "text",
          readOnly: true,
          info: "Calculated upon save",
        },
        {
          label: "Estimated Time (mins)",
          path: "estimatedTime",
          type: "text",
          readOnly: true,
          info: "Calculated upon save",
        },
      ],
    },
    {
      cols: 2,
      fields: [
        {
          label: "Created By",
          path: "createdBy",
          type: "text",
          readOnly: true,
        },
        {
          label: "Last Modified By",
          path: "lastModifiedBy",
          type: "text",
          readOnly: true,
        },
      ],
    },
  ];

  const saveStep = (shouldKeepOpen = false) => {
    // Use centralized validation
    // Use qualificationStepId if available (from searchable field), otherwise qualificationStep
    const qualificationStepId =
      form.qualificationStepId || form.qualificationStep;
    const projectId = form.projectId || form.project || metadata?.id;

    const validationPayload = {
      info: {
        projectId: projectId,
        qualificationStepId: qualificationStepId,
        funnel: form.funnel,
        stepNumber: form.stepNumber,
        numberOfAttempts: form.numberOfAttempts,
        estimatedTimeMinutes: form.estimatedTime || "",
      },
    };

    const validationErrors = validateProjectQualStepEditForm(validationPayload);

    // Convert centralized validation errors to wizard format with field labels
    const errors = [];
    Object.entries(validationErrors).forEach(([path, message]) => {
      const fieldLabelMap = {
        "info.projectId": "Project",
        "info.qualificationStepId": "Qualification Step",
        "info.funnel": "Funnel",
        "info.stepNumber": "Step Number",
        "info.numberOfAttempts": "Number of Attempts",
        "info.estimatedTimeMinutes": "Estimated Time",
      };
      const fieldMap = {
        "info.projectId": "project",
        "info.qualificationStepId": "qualificationStep",
        "info.funnel": "funnel",
        "info.stepNumber": "stepNumber",
        "info.numberOfAttempts": "numberOfAttempts",
        "info.estimatedTimeMinutes": "estimatedTime",
      };
      const field = fieldMap[path] || path;
      const fieldLabel = fieldLabelMap[path] || path;

      // Add field label prefix for messages that don't already reference a field
      const needsFieldLabel = [
        "This field is required",
        "Must be an integer",
        "Must be a number",
        "Invalid format",
      ].includes(message);

      const finalMessage = needsFieldLabel
        ? `${fieldLabel}: ${message}`
        : message;

      errors.push({ field, message: finalMessage });
    });

    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }

    setValidationErrors([]);

    const newItem = {
      ...form,
      id: Date.now(),
      // Ensure we store the ID, not the display name
      qualificationStepId: form.qualificationStepId || form.qualificationStep,
      projectId: form.projectId || form.project || metadata?.id,
      objectiveId: form.objectiveId || form.objective,
    };

    const updatedSteps = Array.isArray(qualificationSteps)
      ? [...qualificationSteps, newItem]
      : [newItem];

    dispatch(updateCreationQualificationSteps(updatedSteps));

    if (!shouldKeepOpen) {
      setShowModal(false);
      resetForm();
    } else {
      resetForm();
    }
  };

  const resetForm = () => {
    setForm({
      id: undefined,
      name: "",
      project: metadata?.name || "",
      projectId: metadata?.id || "",
      objective: "",
      objectiveId: "",
      passingScore: "",
      status: "Draft",
      qualificationStep: "",
      qualificationStepId: "",
      funnel: "A",
      stepNumber: "",
      numberOfAttempts: "1",
      estimatedTime: "",
      createdBy: "",
      lastModifiedBy: "",
    });
    setValidationErrors([]);
  };

  const openModal = () => {
    // Reset and prefill defaults
    const base = {
      id: undefined,
      name: "",
      project: metadata?.name || "",
      projectId: metadata?.id || "",
      objective: "",
      objectiveId: "",
      passingScore: "",
      status: "Draft",
      qualificationStep: "",
      qualificationStepId: "",
      funnel: "A",
      stepNumber: "",
      numberOfAttempts: "1",
      estimatedTime: "",
      createdBy: "",
      lastModifiedBy: "",
    };
    setForm(base);
    setValidationErrors([]);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  const handleChange = (path, value) => {
    setForm((prev) => ({ ...prev, [path]: value }));
  };

  const handleRemove = (id) => {
    if (
      window.confirm("Are you sure you want to remove this qualification step?")
    ) {
      dispatch(removeCreationQualificationStep(id));
    }
  };

  const handleEdit = (step) => {
    const patched = {
      ...step,
      project: metadata?.name || step.project || "",
      projectId: metadata?.id || step.projectId || "",
    };
    setForm(patched);
    setShowModal(true);
    setValidationErrors([]);
  };

  const handleSaveEdit = (shouldKeepOpen = false) => {
    // Use centralized validation
    // Use qualificationStepId if available (from searchable field), otherwise qualificationStep
    const qualificationStepId =
      form.qualificationStepId || form.qualificationStep;
    const projectId = form.projectId || form.project || metadata?.id;

    const validationPayload = {
      info: {
        projectId: projectId,
        qualificationStepId: qualificationStepId,
        funnel: form.funnel,
        stepNumber: form.stepNumber,
        numberOfAttempts: form.numberOfAttempts,
        estimatedTimeMinutes: form.estimatedTime || "",
      },
    };

    const validationErrors = validateProjectQualStepEditForm(validationPayload);

    // Convert centralized validation errors to wizard format with field labels
    const errors = [];
    Object.entries(validationErrors).forEach(([path, message]) => {
      const fieldLabelMap = {
        "info.projectId": "Project",
        "info.qualificationStepId": "Qualification Step",
        "info.funnel": "Funnel",
        "info.stepNumber": "Step Number",
        "info.numberOfAttempts": "Number of Attempts",
        "info.estimatedTimeMinutes": "Estimated Time",
      };
      const fieldMap = {
        "info.projectId": "project",
        "info.qualificationStepId": "qualificationStep",
        "info.funnel": "funnel",
        "info.stepNumber": "stepNumber",
        "info.numberOfAttempts": "numberOfAttempts",
        "info.estimatedTimeMinutes": "estimatedTime",
      };
      const field = fieldMap[path] || path;
      const fieldLabel = fieldLabelMap[path] || path;

      // Add field label prefix for messages that don't already reference a field
      const needsFieldLabel = [
        "This field is required",
        "Must be an integer",
        "Must be a number",
        "Invalid format",
      ].includes(message);

      const finalMessage = needsFieldLabel
        ? `${fieldLabel}: ${message}`
        : message;

      errors.push({ field, message: finalMessage });
    });

    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }

    setValidationErrors([]);

    const updatedSteps = qualificationSteps.map((step) =>
      step.id === form.id
        ? {
            ...form,
            // Ensure we store the ID, not the display name
            qualificationStepId:
              form.qualificationStepId || form.qualificationStep,
            projectId: form.projectId || form.project || metadata?.id,
            objectiveId: form.objectiveId || form.objective,
          }
        : step,
    );

    dispatch(updateCreationQualificationSteps(updatedSteps));

    if (!shouldKeepOpen) {
      setShowModal(false);
      resetForm();
    } else {
      resetForm();
    }
  };

  return (
    <div className="fade-in">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Qualification Steps
        </h2>
        <p className="text-gray-600">
          Define qualification requirements for contributors
        </p>
      </div>

      {/* Current Steps List */}
      {qualificationSteps.length > 0 && (
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">
            Current Qualification Steps ({qualificationSteps.length})
          </h3>
          <div className="space-y-3">
            {qualificationSteps.map((step) => (
              <div
                key={step.id}
                className="p-4 bg-gray-50 border border-gray-200 rounded-lg flex items-center justify-between group"
              >
                <div className="flex-1">
                  <div className="font-medium text-gray-900">{step.name}</div>
                  <div className="text-sm text-gray-600 mt-1">
                    Step {step.stepNumber} | Funnel: {step.funnel} | Attempts:{" "}
                    {step.numberOfAttempts} | Status: {step.status}
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <button
                    type="button"
                    onClick={() => handleEdit(step)}
                    className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                    title="Edit qualification step"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleRemove(step.id)}
                    className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                    title="Remove qualification step"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* New Step Button */}
      <button
        type="button"
        onClick={openModal}
        className="flex items-center gap-2 px-4 py-2 text-white rounded-lg transition-colors"
        style={{ backgroundColor: "#08979c" }}
        onMouseEnter={(e) => (e.target.style.backgroundColor = "#0ab0b7")}
        onMouseLeave={(e) => (e.target.style.backgroundColor = "#08979c")}
      >
        <Plus className="w-5 h-5" />
        New Qualification Step
      </button>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-gray-900">
                {form.id ? "Edit Qualification Step" : "New Qualification Step"}
              </h3>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Validation Errors */}
              {validationErrors.length > 0 && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <h4 className="text-red-800 font-semibold mb-2">
                    Please fix the following errors:
                  </h4>
                  <ul className="list-disc list-inside text-red-700 text-sm space-y-1">
                    {validationErrors.map((error, index) => (
                      <li key={index}>{error.message}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Form Fields */}
              {renderGrid(fields, {
                formData: form,
                onChange: handleChange,
                showValidation: validationErrors.length > 0,
              })}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={closeModal}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              {!form.id && (
                <button
                  type="button"
                  onClick={() => saveStep(true)}
                  className="px-4 py-2"
                  style={{
                    backgroundColor: "#08979c",
                    color: "white",
                    borderRadius: "8px",
                  }}
                  onMouseEnter={(e) =>
                    (e.target.style.backgroundColor = "#0ab0b7")
                  }
                  onMouseLeave={(e) =>
                    (e.target.style.backgroundColor = "#08979c")
                  }
                >
                  Save & New
                </button>
              )}
              <button
                type="button"
                onClick={() =>
                  form.id ? handleSaveEdit(false) : saveStep(false)
                }
                className="px-4 py-2"
                style={{
                  backgroundColor: "#08979c",
                  color: "white",
                  borderRadius: "8px",
                }}
                onMouseEnter={(e) =>
                  (e.target.style.backgroundColor = "#0ab0b7")
                }
                onMouseLeave={(e) =>
                  (e.target.style.backgroundColor = "#08979c")
                }
              >
                {form.id ? "Update" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QualificationSteps;
